import axios from 'axios';
import { loadProfile, saveProfile } from '@/utils/storage';
import { resolveAccountAddress } from '@/stores/profile';
import { detectAppType } from '@/utils/device';
import { encryptPostPayload } from '@/utils/encrypt';
import { showToast, showLoadingToast } from 'vant';

const API_PROXY_PREFIX = import.meta.env.VITE_API_PROXY_PREFIX || '/app/api';
const API_PROXY_TARGET = String(
  import.meta.env.VITE_API_PROXY_TARGET || 'https://api.winpay.dev'
).replace(/\/+$/, '');
const API_BASE_URL = import.meta.env.DEV
  ? API_PROXY_PREFIX
  : (import.meta.env.VITE_API_BASE_URL || `${API_PROXY_TARGET}${API_PROXY_PREFIX}`);

function shouldShowLoading(config) {
  return config?.meta?.showLoading !== false;
}

function shouldUseForm(config) {
  return config.headers?.['Content-Type'] === 'application/x-www-form-urlencoded'
    || config.meta?.bodyType === 'form';
}

function shouldUseJson(config) {
  return config.headers?.['Content-Type'] === 'application/json'
    || config.meta?.bodyType === 'json';
}

function isMultipartBody(config) {
  return config.data instanceof FormData;
}

function shouldEncryptPostBody(config) {
  return config.method === 'post'
    && config.meta?.disablePostEncrypt !== true
    && config.data
    && typeof config.data === 'object'
    && !(config.data instanceof FormData)
    && !(config.data instanceof URLSearchParams);
}

function normalizeMessage(payload, fallback = 'Request failed') {
  return payload?.msg || payload?.message || fallback;
}

function createApiError(payload, fallback = 'Request failed') {
  const message = normalizeMessage(payload, fallback);
  const error = new Error(message);
  error.code = payload?.code;
  error.payload = payload;
  error.msg = message;
  return error;
}

const http = axios.create({
  baseURL: API_BASE_URL,
  timeout: 60000
});

http.interceptors.request.use((config) => {
  const profile = loadProfile() || {};
  const currentAddress = resolveAccountAddress(profile.currentAccount, profile.currentNetwork);
  config.headers = config.headers || {};
  config.headers.token = profile.token || '';
  config.headers.lang = profile.locale || 'zh-CN';
  config.headers.appType = detectAppType();
  config.headers.appToken = profile.appToken || '';
  config.headers.app_version = profile.appVersion || import.meta.env.VITE_APP_VERSION || 'h5';

  if (isMultipartBody(config)) {
    delete config.headers['Content-Type'];
    return config;
  }

  if (!config.headers['Content-Type'] && !config.data) {
    config.headers['Content-Type'] = 'application/json';
  }

  if (!config.headers['Content-Type']) {
    config.headers['Content-Type'] = shouldUseForm(config)
      ? 'application/x-www-form-urlencoded'
      : 'application/json';
  }

  // if (config.data && shouldUseForm(config)) {
  //   config.data = toFormBody(config.data);
  // }

  if (config.data && shouldUseJson(config)) {
    config.headers['Content-Type'] = 'application/json';
  }

  if (shouldEncryptPostBody(config)) {
    const payload = {
      ...config.data,
      time: Date.now(),
      address: config.data.userAddress || config.data.address || currentAddress || ''
    };
    config.headers.Auth = encryptPostPayload(JSON.stringify(payload));
    config.data = {};
  }

  return config;
});

http.interceptors.response.use(
  (response) => {
    const payload = response.data;
    if (!payload || typeof payload.code === 'undefined') {
      return payload;
    }

    if (payload.code === 200) {
      return Object.prototype.hasOwnProperty.call(payload, 'data')
        ? payload.data
        : payload;
    }

    if (payload.code === 401) {
      const profile = loadProfile() || {};
      saveProfile({ ...profile, token: '' });
    }

    setTimeout(() => {
      const message = normalizeMessage(payload);
      showToast({
        message,
        icon: 'close',
        wordBreak: 'normal',
        duration: 4000
      });
    }, 100);

    return Promise.reject(createApiError(payload));
  },
  (error) => {
    const payload = error?.response?.data;
    const message = normalizeMessage(payload, error?.msg || 'Network error');
    showToast({
      message,
      icon: 'close',
      wordBreak: 'normal',
      duration: 4000
    });
    return Promise.reject(createApiError(payload, message));
  }
);

http.postJson = function postJson(url, data = {}, config = {}) {
  const toast = shouldShowLoading(config)
    ? showLoadingToast({
      message: 'Ai Thinking ...',
      forbidClick: true,
      overlay: true,
      loadingType: 'spinner',
      className: 'loading_spinner',
      duration: 0
    })
    : null;
  return http.post(url, data, {
    ...config,
    meta: {
      ...(config.meta || {}),
      bodyType: 'json'
    },
    headers: {
      ...(config.headers || {}),
      'Content-Type': 'application/json'
    }
  }).finally(() => {
    toast?.close();
  });
};

http.postForm = function postForm(url, data = {}, config = {}) {
  const toast = shouldShowLoading(config)
    ? showLoadingToast({
      message: 'Ai Thinking ...',
      forbidClick: true,
      overlay: true,
      duration: 0
    })
    : null;
  return http.post(url, data, {
    ...config,
    meta: {
      ...(config.meta || {}),
      bodyType: 'form'
    },
    headers: {
      ...(config.headers || {}),
      'Content-Type': 'application/x-www-form-urlencoded'
    }
  }).finally(() => {
    toast?.close();
  });
};

http.postMultipart = function postMultipart(url, data, config = {}) {
  const toast = shouldShowLoading(config)
    ? showLoadingToast({
      message: 'Ai Thinking ...',
      forbidClick: true,
      overlay: true,
      duration: 0
    })
    : null;
  return http.post(url, data, {
    ...config,
    meta: {
      ...(config.meta || {}),
      bodyType: 'multipart'
    },
    headers: {
      ...(config.headers || {})
    }
  }).finally(() => {
    toast?.close();
  });
};

export default http;
